/**
 * i18n entry point. Provides:
 *   - useLanguage() — React hook returning the current language code and a setter
 *   - useT() — React hook returning the translation function bound to the
 *              current language
 *   - t(key, vars?) — pure translation function used outside React (e.g.
 *                      from draftingHelpers prompts which run in a worker
 *                      reducer context)
 *   - setLanguage(lang) — programmatic language switcher; persists to
 *                          localStorage so the next page load picks the
 *                          same language
 *
 * Persistence: language is stored under localStorage key "edit-dwg.lang".
 * Russian is the default per project requirements.
 */

import { create } from "zustand";

import {
  DEFAULT_LANGUAGE,
  messages,
  SUPPORTED_LANGUAGES,
  type LanguageCode,
  type MessageKey,
} from "./messages";

const LOCAL_STORAGE_KEY = "edit-dwg.lang";

function readPersistedLanguage(): LanguageCode {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  try {
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored && (SUPPORTED_LANGUAGES as string[]).includes(stored)) {
      return stored as LanguageCode;
    }
  } catch {
    // ignore — localStorage may be unavailable in private mode
  }
  return DEFAULT_LANGUAGE;
}

function persistLanguage(lang: LanguageCode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, lang);
  } catch {
    // ignore
  }
}

type LanguageStore = {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
};

const useLanguageStore = create<LanguageStore>((set) => ({
  language: readPersistedLanguage(),
  setLanguage: (lang) => {
    persistLanguage(lang);
    set({ language: lang });
  },
}));

/**
 * Pure translation function. Use this OUTSIDE React components — for
 * example from reducers, drafting helpers, command-line summary builders.
 *
 * Inside a component prefer `useT()` so the component re-renders when
 * the language changes.
 */
export function t(
  key: MessageKey,
  vars?: Record<string, string | number>,
  language?: LanguageCode,
): string {
  const lang = language ?? useLanguageStore.getState().language;
  let template =
    messages[lang]?.[key] ?? messages[DEFAULT_LANGUAGE][key] ?? key;
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      template = template.replace(
        new RegExp(`\\{${name}\\}`, "g"),
        String(value),
      );
    }
  }
  return template;
}

/**
 * React hook returning the current language code and a setter. Components
 * that need to switch the language read this directly.
 */
export function useLanguage(): {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
} {
  const language = useLanguageStore((state) => state.language);
  const setLanguage = useLanguageStore((state) => state.setLanguage);
  return { language, setLanguage };
}

/**
 * React hook returning the bound `t()` function. Components that just
 * need to translate strings use this — it triggers re-renders when the
 * language changes (via the underlying zustand subscription).
 */
export function useT(): (
  key: MessageKey,
  vars?: Record<string, string | number>,
) => string {
  const language = useLanguageStore((state) => state.language);
  return (key, vars) => t(key, vars, language);
}

/**
 * Pure language setter — sets the current language outside React (e.g.
 * from unit tests that need to assert localised audit summaries).
 * Persists the choice to `localStorage` so the next page load picks it
 * up.
 */
export function setLanguage(lang: LanguageCode): void {
  useLanguageStore.getState().setLanguage(lang);
}

/**
 * Pure language getter — reads the current language outside React, used by
 * helpers that need locale-aware behaviour beyond simple string lookup
 * (e.g. `Intl.PluralRules` selection in the import-status formatter).
 */
export function getActiveLanguage(): LanguageCode {
  return useLanguageStore.getState().language;
}

export type { LanguageCode, MessageKey };
export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };
